/**
 * Centralized Telegram message templates
 * All bot messages are defined here for easy maintenance
 */

import { escapeHtml, formatDateTime, formatCurrency } from './utils.js';
import { BookingKeyboards, denialReasonLabel, type InlineKeyboard } from './keyboards.js';
import { shouldShowPasscodeLine, virtualPlatformLabel } from '../location/virtual-platform.js';
import { phoneCallLine, type PhoneCall } from '../location/by-phone.js';
import { telegramHtmlToTipTap } from '../rich-text/tiptap-html.js';
import { tiptapToTelegramHtml } from './tiptap-to-telegram-html.js';
import { getTelegramVisibleLengthFromHtml } from './tiptap-visible-length.js';
import { BOOKING_FIRST_NAME_LENGTH, boundBookingFact, splitPreparationText } from './booking-message-content.js';
import { bookingStatusDate, statusLine } from './booking-status-line.js';
import { formatNoteLine, type SentMessageCopy } from './client-communication-messages.js';
import type { BookingWaitingItems } from './booking-waiting.js';
import { formatUsd } from '../shop/money.js';
import { shownOptionLabel } from '../shop/line-label.js';
import type { ShopEmailItem, ShopProblemInput, ShopRefundedInput, ShopRefundFailedInput, ShopShippedInput } from '../email/templates.js';
import type { ShopUpdateEmail } from '../client-outbox/shop-message.contract.js';
import type {
  BookingConfirmationData,
  BookingJoinFacts,
  BookingPaymentCompletedData,
} from './booking-types.js';

export interface MessageTemplate {
  text: string;
  disableNotification?: boolean;
  keyboard?: any; // Using any to match Grammy's keyboard types
  parse_mode?: 'HTML' | 'Markdown' | 'MarkdownV2';
  /**
   * chat-relay-reply D2: send with NO Telegram parse mode, so the text reaches the client literally
   * (the sender otherwise defaults to HTML). The stored copy records `parseMode: 'plain'`.
   */
  plainText?: boolean;
  /**
   * in-bubble-buttons D7: keep the under-bubble keyboard (plain `sendMessage` with
   * `reply_markup`). Only the broadcast templates set it; broadcasts are out of the MVP.
   */
  legacyKeyboard?: boolean;
}

export interface NotificationSlotDeliveryData {
  recipientFirstName?: string | null;
}

function sanitizeNotificationHtml(text: string): string {
  const doc = telegramHtmlToTipTap(text);
  for (const paragraph of doc.content ?? []) for (const node of paragraph.content ?? []) {
    node.marks = node.marks?.filter(mark => mark.type === 'bold' || mark.type === 'italic');
  }
  // Parsing and serializing also balances malformed legacy tags, escapes bare
  // angle brackets/entities and emits Telegram-supported newlines, never <br>.
  return tiptapToTelegramHtml(doc);
}

/**
 * Applies send-time personalization to either a reminder or follow-up template.
 * No personal chat button: a client reaches the business by messaging the bot
 * (chat-relay-reply, Gabriel 2026-10-02), whatever a slot's stored
 * `includeChatButton` says.
 */
export function assembleNotificationSlotMessage(
  template: MessageTemplate,
  data: NotificationSlotDeliveryData,
): MessageTemplate {
  const personalizedText = template.text.replace(
    /\{firstName\}/g,
    escapeHtml(data.recipientFirstName ?? ''),
  );
  const text = template.parse_mode === 'HTML'
    ? sanitizeNotificationHtml(personalizedText)
    : personalizedText;
  const existingRows = Array.isArray(template.keyboard?.inline_keyboard)
    ? [...template.keyboard.inline_keyboard]
    : [];

  return {
    ...template,
    text,
    ...(existingRows.length > 0
      ? { keyboard: { ...template.keyboard, inline_keyboard: existingRows } }
      : { keyboard: undefined }),
  };
}

export const FollowUpMessages = {
  /** No notification slot ships default copy: an empty body is never substituted, it is never scheduled. */
  followUp(data: NotificationSlotDeliveryData & { message: string; formUrl?: string }): MessageTemplate {
    return assembleNotificationSlotMessage({ text: data.message, parse_mode: 'HTML',
      ...(data.formUrl ? { keyboard: { inline_keyboard: [[{ text: 'Open form', web_app: { url: data.formUrl }, style: 'primary' }]] } } : {}),
    }, data);
  },
};

/**
 * The three facts the welcome and /help teach (mvp-silent-moments D8, Gabriel
 * 2026-10-09). `brand` and `who` arrive escaped.
 */
function chatFacts(brand: string, who: string): string {
  return `<b>This chat is ${brand}.</b> Anything you type here goes straight to ${who}, and they answer you right here.

<b>Your bookings land here too.</b> Confirmations, reminders and changes arrive as messages, each with a button that opens the app at the right place.

<b>The app</b> is where you book, see your schedule and pay. Open it any time with the blue <b>Booking Hub</b> button in the bottom left corner, next to the message box, or with the button below.`;
}

/** Config for welcome message template */
export interface WelcomeMessageConfig {
  firstName: string;
  brandName: string;
  bookingHubUrl: string;
  /** Admin/practitioner first name for the "message {adminFirstName}" line (optional). */
  adminFirstName?: string | null;
}

/**
 * Registration-related messages
 */
export const RegistrationMessages = {
  /**
   * Welcome message for all users (registered or not). mvp-silent-moments D8
   * (Gabriel 2026-10-09): three facts in order. This chat reaches the business
   * and is answered here; booking messages land here with buttons into the app;
   * the app opens any time from the Booking Hub button or the button below.
   */
  welcome: (config: WelcomeMessageConfig): MessageTemplate => {
    const brand = escapeHtml(config.brandName);
    const messageWho = config.adminFirstName ? escapeHtml(config.adminFirstName) : brand;
    return {
      text: `Welcome to ${brand}, ${escapeHtml(config.firstName)} 👋

${chatFacts(brand, messageWho)}`,
      keyboard: {
        inline_keyboard: [
          [{ text: `Open ${config.brandName}`, web_app: { url: config.bookingHubUrl }, style: 'primary' }],
        ],
      },
      parse_mode: 'HTML',
    };
  },
};

/** Client conversation replies shared by production handlers and live-fire tests. */
export const ClientConversationMessages = {
  /** The welcome's three facts, in the same order, under "How {Brand} works" (mvp-silent-moments D8). */
  help(config: { brandName: string; messageWho: string; appUrl: string }): MessageTemplate {
    const brand = escapeHtml(config.brandName);
    const who = escapeHtml(config.messageWho);
    return {
      text: `<b>How ${brand} works</b>

${chatFacts(brand, who)}

Type /help anytime to see this again.`,
      keyboard: { inline_keyboard: [[{ text: 'Open the app', web_app: { url: config.appUrl }, style: 'primary' }]] },
      parse_mode: 'HTML',
    };
  },

  /** The retired Contact button's alert (plain text, a callback alert). */
  retiredContact(config: { brandName: string }): MessageTemplate {
    return { text: `Just type your message here. ${config.brandName} reads it and answers you in this chat.` };
  },

  /** The retired My bookings button's alert (plain text, a callback alert). */
  retiredBookings(): MessageTemplate {
    return { text: 'Your bookings live in the app. Open it with the Booking Hub button.' };
  },
} as const;

/**
 * The business-booked Booked message's closing line (manual-booking-redesign
 * D15). Exported so the admin chat's locked preview (chatAttachment.ts) renders
 * the same words the client receives.
 */
export const BOOKED_DETAILS_FOOTER = 'Your booking details are below.';

/**
 * Admin notification messages
 */
export const AdminMessages = {
  afterActionAttendancePrompt(
    instanceTitle: string,
    participantCount: number,
    detailUrl: string,
    hasEnabledFollowUps = false,
  ): MessageTemplate {
    const people = participantCount === 1 ? '1 person' : `${participantCount} people`;
    return {
      text: `<b>${escapeHtml(instanceTitle)}</b> just ended.\nMark attendance for ${people}.` +
        (hasEnabledFollowUps ? "\nFollow-ups won't send until attendance is marked." : ''),
      keyboard: {
        inline_keyboard: [[{ text: 'Mark attendance', web_app: { url: detailUrl }, style: 'primary' }]],
      },
      parse_mode: 'HTML',
    };
  },

  /**
   * Admin promotion invite message (T-P5-D1-F1-9)
   * Sent via BOOKINGS bot after CLI promotion to invite admin to start Admin Bot.
   * Contains deep link to Admin Bot with onboarding payload.
   */
  adminPromotionInvite: (adminBotUsername: string): MessageTemplate => ({
    text: `🎉 <b>You've been promoted to Admin!</b>

You now have access to the Admin Dashboard where you can:
• View and manage bookings
• See client information
• Receive real-time notifications

Tap the button below to get started.`,
    keyboard: {
      inline_keyboard: [
        [{ text: '🚀 Start Admin Bot', url: `https://t.me/${adminBotUsername}?start=admin_onboard`, style: 'primary' }],
      ],
    },
    parse_mode: 'HTML',
  }),

  /**
   * Welcome message for Admin Bot /start command (T-P5-D1-F1-7)
   * Sent via Admin Bot to admins who have started the bot
   */
  adminBotWelcome: (firstName: string, dashboardUrl: string, brandName?: string): MessageTemplate => {
    const brandLabel = brandName ? `${escapeHtml(brandName)} ` : '';
    return {
      text: `<b>You're connected, ${escapeHtml(firstName)}</b> 🎛️

This chat is your ${brandLabel}alert line: new bookings, approval requests, payments, and cancellations land here the moment they happen, plus a daily summary of how business is going.

Your dashboard is where the work happens:`,
      keyboard: {
        inline_keyboard: [
          [{ text: 'Open dashboard', web_app: { url: dashboardUrl }, style: 'primary' }],
        ],
      },
      parse_mode: 'HTML',
    };
  },

  /**
   * manual-booking-redesign D17: the admin bot's Approve refused for a waiting
   * manual booking that starts in under a minute (no time is left for the
   * client to pay online). Plain text: shown as the callback alert.
   */
  approvalNoCompletionTime: (): MessageTemplate => ({
    text: 'Too close to the start for the client to pay online. Deny it, or book them in person.',
  }),
};

/**
 * How long the client's booking runs, in words they can act on.
 *
 * A CONTINUOUS multi-day event stores its whole run as minutes (days × 1440 —
 * `wizard.mapper` "Continuous: duration = total minutes for all days"), and that
 * number travels all the way to `Booking.duration`. Printed raw, the person who
 * just paid for a three-day retreat was told their booking was "4320 min"
 * (smoke 2026-08-19).
 *
 * Any whole number of days is stated in days. That is deliberately shape-blind:
 * these templates receive a minute count and no structure, and a 1:1 session
 * that genuinely runs 1440 minutes reads better as "1 day" than as "1440 min"
 * either way. Anything that is not a whole day keeps minutes, unchanged.
 */
export function formatBookedLength(minutes: number): string {
  if (minutes >= MINUTES_PER_DAY && minutes % MINUTES_PER_DAY === 0) {
    const days = minutes / MINUTES_PER_DAY;
    return `${days} ${days === 1 ? 'day' : 'days'}`;
  }
  return `${minutes} min`;
}

/**
 * Booking Flow Messages
 *
 * Message templates for the Telegram booking flow UI.
 * All user-provided content is escaped using escapeHtml().
 * Keyboards are built separately by BookingKeyboards and combined at render time.
 */
interface BookingStatusFacts {
  serviceName: string;
  practitionerName: string;
  dateTime: Date | string;
  timezone: string;
  duration: number;
  hideNameInMessages?: boolean;
  bookingDetailUrl?: string;
  /** booking-directions-button: Google Maps directions to an in-person booking's place; absent online or with no address. */
  directionsUrl?: string | null;
}

/** "Wednesday · Deep Tissue Massage · 60 min · with Maya": the Booked message's session line, shared by the release message. */
function bookedSessionLine(facts: BookingStatusFacts): string {
  const service = facts.hideNameInMessages ? 'your appointment' : boundBookingFact(facts.serviceName, 120);
  return `${bookingStatusDate(facts.dateTime, facts.timezone).toFormat('cccc')} · ${escapeHtml(service)} · ${formatBookedLength(facts.duration)} · with ${escapeHtml(boundBookingFact(facts.practitionerName, 60))}`;
}

function bookingStatusHeading(verb: 'Booked' | 'Moved', facts: BookingStatusFacts): string {
  return `<b>${statusLine(verb, facts.dateTime, facts.timezone)}</b>\n${bookedSessionLine(facts)}`;
}

/** manual-booking-redesign D7: the session line of the waiting message (no status line, never pinned). */
function bookingSessionLine(facts: Omit<BookingStatusFacts, 'duration'>): string {
  const service = facts.hideNameInMessages ? 'your appointment' : boundBookingFact(facts.serviceName, 120);
  const start = bookingStatusDate(facts.dateTime, facts.timezone);
  return `${start.toFormat('cccc, LLL d')} · ${start.toFormat('h:mm a')} · ${escapeHtml(service)} · with ${escapeHtml(boundBookingFact(facts.practitionerName, 60))}`;
}

/**
 * virtual-sessions-finish D9: "Passcode: 482 913" when a link resolves and the
 * passcode is not already inside it; empty otherwise.
 */
function passcodeLine(join: BookingJoinFacts | undefined): string {
  if (!join?.link || !shouldShowPasscodeLine(join.link, join.passcode)) return '';
  return `Passcode: ${escapeHtml((join.passcode ?? '').trim())}`;
}

/** D9: the lines under the Booked session line: "Online on Zoom" (or "Online"), then the passcode line. */
function onlineFactLines(join: BookingJoinFacts | undefined): string {
  if (!join) return '';
  const label = virtualPlatformLabel(join.platform);
  const passcode = passcodeLine(join);
  return `\n${label ? `Online on ${escapeHtml(label)}` : 'Online'}${passcode ? `\n${passcode}` : ''}`;
}

/**
 * booking-directions-button: Get directions beside the message's first button (View my booking),
 * one row ("i wanted them side by side", Gabriel 2026-10-15); its own row when there is none.
 */
function withDirectionsRow(keyboard: InlineKeyboard | undefined, url: string | null | undefined): InlineKeyboard | undefined {
  if (!url) return keyboard;
  const [first = [], ...rest] = keyboard?.inline_keyboard ?? [];
  return { inline_keyboard: [[BookingKeyboards.directionsButton(url), ...first], ...rest] };
}

/**
 * service-by-phone D8: "Dana Wilcher will call you at +1 555 010 0199." where the online line sits, directly under
 * the line naming the service; a missing number drops "at <number>" instead of failing the send.
 */
function phoneCallLines(phoneCall: PhoneCall | null | undefined): string {
  return phoneCall ? `\n${escapeHtml(phoneCallLine(boundBookingFact(phoneCall.caller, 120), phoneCall.phone))}` : '';
}

/** location-notes-to-client: the in-person place, then the location notes, under the Booked session line. */
function placeLines(facts: { place?: string | null; locationNotes?: string | null }): string {
  const place = facts.place?.trim(), notes = facts.locationNotes?.trim();
  return (place ? `\n${escapeHtml(boundBookingFact(place, 200))}` : '') + (notes ? `\n<i>${escapeHtml(notes)}</i>` : '');
}


/** D10: the Join row above the message's other buttons, only when a link resolves. */
function withJoinRow(keyboard: InlineKeyboard | undefined, join: BookingJoinFacts | undefined): InlineKeyboard | undefined {
  if (!join?.link) return keyboard;
  const row = BookingKeyboards.joinMeetingRow(join.link, virtualPlatformLabel(join.platform));
  return { inline_keyboard: [row, ...(keyboard?.inline_keyboard ?? [])] };
}

const COUNT_WORDS = ['No', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten'];

/** "One form", "Two forms". */
function formCount(n: number): string {
  return `${COUNT_WORDS[n] ?? String(n)} ${n === 1 ? 'form' : 'forms'}`;
}

/** "$140", "$92.50". */
function wholeDollars(amount: number): string {
  return Number.isInteger(amount) ? `$${amount}` : `$${amount.toFixed(2)}`;
}

/** A one-form button names the form when it fits the button. */
const SHORT_FORM_NAME = 24;

/** The waiting message's one adaptive button (D7); the admin's locked preview shows the same label. */
export function bookingWaitingLabel(items: BookingWaitingItems): string {
  if (items.approval) return 'Fill out forms';
  if (items.registration && items.forms.length === 0 && !items.payment) return 'Register';
  if (!items.registration && items.forms.length === 0 && items.payment) {
    return items.payment.amount != null ? `Pay ${wholeDollars(items.payment.amount)}` : 'Pay now';
  }
  if (!items.registration && items.forms.length === 1 && !items.payment) {
    return items.forms[0]!.length <= SHORT_FORM_NAME ? `Complete ${items.forms[0]}` : 'Complete intake';
  }
  return 'Complete booking';
}

export interface BookingWaitingMessageData {
  serviceName: string;
  practitionerName: string;
  dateTime: Date | string;
  timezone: string;
  hideNameInMessages?: boolean;
  firstName?: string | null;
  /** `bookingSenderName`: the acting admin's first name, else the business name. */
  senderName: string;
  /** `bookingDeadlineText` of `Booking.completeBy`. */
  deadline: string;
  items: BookingWaitingItems;
  /** The admin's chat note, rendered literally. */
  note?: string | null;
  /** The button's destination (`bookingWaitingAction`); the preview renders without it. */
  actionUrl?: string;
}

export type BookingWaitingClosedOutcome = 'completed' | 'sent_for_approval' | 'released' | 'cancelled';

/** The sessions a ban message lists before "and N more" (Telegram's text cap). */
const BAN_MESSAGE_SESSION_LIMIT = 40;

export const BookingFlowMessages = {
  /**
   * Booking confirmation message sent after successful booking.
   */
  bookingConfirmed(params: BookingConfirmationData): MessageTemplate {
    const suffix = params.firstName ? `, ${escapeHtml(boundBookingFact(params.firstName, BOOKING_FIRST_NAME_LENGTH))}` : '';
    let body = params.approved ? `Your request was approved${suffix}. Your spot is locked in.` : '';
    // manual-booking-redesign D7: the business-booked message names the acting admin, else the business.
    const booker = params.adminFirstName || params.brandName || 'Your practitioner';
    if (params.bookedBy === 'business') body = `${escapeHtml(boundBookingFact(booker, 120))} booked you in${suffix}.`;
    if (params.formsCompleted) body = params.registrationOnly ? `You're registered${suffix}.` : `Your forms are in${suffix}.`;
    const inPerson = params.effectivePayment ? params.effectivePayment === 'in_person' : params.paymentRequirement === 'PAY_AT_SESSION';
    if (!params.approved && inPerson) {
      body += (body ? ' ' : '') + 'Payment is collected at your session.' + (params.bookedBy ? '' : ' Nothing to do now.');
    }
    if (params.paymentProvider === 'STRIPE' && params.paymentStatus === 'PAID' && params.paymentAmount != null) {
      body += (body ? '\n\n' : '') + `Paid: $${params.paymentAmount.toFixed(2)}`;
    }
    if (params.bookedBy === 'business' && params.note) body += `\n\n${formatNoteLine(escapeHtml(params.note))}`;
    if (params.bookedBy === 'business' && !params.formsCompleted) body += `\n\n${BOOKED_DETAILS_FOOTER}`;
    const url = params.bookingDetailUrl;
    return { text: bookingStatusHeading('Booked', params) + onlineFactLines(params.join) + phoneCallLines(params.phoneCall) + placeLines(params)
      + (body ? `\n\n${body}` : ''),
      keyboard: withJoinRow(withDirectionsRow(url ? BookingKeyboards.viewMyBooking(url) : undefined, params.directionsUrl), params.join), parse_mode: 'HTML' };
  },

  bookingPending(params: BookingConfirmationData): MessageTemplate {
    const { serviceName, practitionerName, dateTime, timezone, firstName, brandName, bookingDetailUrl, paymentRequirement } = params;
    const suffix = firstName ? `, ${escapeHtml(firstName)}` : '';
    const confirmer = brandName ? escapeHtml(brandName) : 'Your practitioner';
    let text = `<b>Request sent${suffix} ⏳</b>\n\n` +
      `${confirmer} will confirm shortly. We'll message you the moment it's approved.\n\n` +
      `<b>${params.hideNameInMessages ? 'Your appointment' : escapeHtml(serviceName)}</b>\n${formatDateTime(dateTime, timezone)} · with ${escapeHtml(practitionerName)}`;
    if (paymentRequirement === 'PAY_AT_SESSION') text += '\n\nIf approved, payment is collected at your session.';
    return { text, keyboard: bookingDetailUrl ? BookingKeyboards.viewMyBooking(bookingDetailUrl) : undefined, parse_mode: 'HTML' };
  },

  /**
   * Booking denied notification to client. A denial does NOT imply other times
   * are open (copy-verdicts A1), so no rebook button, and no personal chat button
   * either: the client replies to the bot (chat-relay-reply, Gabriel 2026-10-02).
   */
  bookingDenied(params: {
    serviceName: string;
    /** The requested session time, shown on its own line under the title (Gabriel 2026-10-02). */
    dateTime?: Date | string;
    timezone?: string;
    /**
     * A bot reason code renders as its label (never the raw code); free text
     * renders as written; none (the session page's reasonless Deny) renders the
     * default wording.
     */
    reason?: string | null;
    firstName?: string | null;
    /** booking-truth-batch: a discreet offering is never named. */
    hideNameInMessages?: boolean;
  }): MessageTemplate {
    const { serviceName, dateTime, timezone, firstName } = params;
    const reason = params.reason || 'Your booking request could not be accommodated at this time.';
    const lead = firstName ? `${escapeHtml(firstName)}, this` : 'This';
    const when = dateTime && timezone ? `\n${formatDateTime(dateTime, timezone)}` : '';

    return {
      text:
        `<b>About your ${params.hideNameInMessages ? 'appointment' : escapeHtml(serviceName)} request</b>${when}\n\n` +
        `${lead} time didn't work out: ${escapeHtml(denialReasonLabel(reason))}\n\n` +
        `Please contact us if you have any questions.`,
      parse_mode: 'HTML',
    };
  },

  /**
   * booking-request-expiry D3: the request was still unanswered at its start, so
   * the system cancelled it. Unlike a denial the business never said no, so it
   * offers Book another time. Never pinned.
   */
  bookingRequestExpired(params: {
    serviceName: string;
    dateTime: Date | string;
    timezone: string;
    firstName?: string | null;
    hideNameInMessages?: boolean;
    bookUrl?: string;
  }): MessageTemplate {
    const lead = params.firstName ? `${escapeHtml(params.firstName)}, this` : 'This';
    return {
      text:
        `<b>About your ${params.hideNameInMessages ? 'appointment' : escapeHtml(params.serviceName)} request</b>\n` +
        `${formatDateTime(params.dateTime, params.timezone)}\n\n` +
        `${lead} request wasn't confirmed in time, so it's been cancelled.`,
      parse_mode: 'HTML',
      keyboard: params.bookUrl ? { inline_keyboard: [[{ text: 'Book another time', web_app: { url: params.bookUrl }, style: 'primary' }]] } : undefined,
    };
  },

  /**
   * Booking cancelled notification to client (P5-D17-F6 §7.2). For a CONFIRMED
   * booking cancelled by the admin — NEVER bookingDenied's "request could not be
   * accommodated" language (that's for a request, this was a confirmed booking).
   */
  bookingCancelled(params: {
    serviceName: string;
    dateTime: Date | string;
    timezone: string;
    reason?: string;
    firstName?: string | null;
    /** Deep link to the client `/book` page for the [Book again] doorbell. */
    bookUrl?: string;
    /** True when the client withdrew a pending request rather than cancelling a booking */
    isRequestWithdrawal?: boolean;
    /** booking-truth-batch: a discreet offering is never named. */
    hideNameInMessages?: boolean;
  }): MessageTemplate {
    const { serviceName, dateTime, timezone, reason, firstName, bookUrl, isRequestWithdrawal } = params;
    const formattedDate = formatDateTime(dateTime, timezone);
    const lead = firstName ? `${escapeHtml(firstName)}, your` : 'Your';

    // Withdrawal keeps its distinct "request" semantics (cancel-pending-request)
    // rendered in the copy-pass voice: no emoji header, personalized lead.
    const hidden = params.hideNameInMessages === true;
    let text = isRequestWithdrawal
      ? `<b>Request cancelled</b>\n\n` +
        (hidden
          ? `${lead} request for ${formattedDate} was successfully cancelled.`
          : `${lead} request for <b>${escapeHtml(serviceName)}</b> on ${formattedDate} was successfully cancelled.`)
      : `<b>Booking cancelled</b>\n\n` +
        (hidden
          ? `${lead} appointment on ${formattedDate} has been cancelled.`
          : `${lead} <b>${escapeHtml(serviceName)}</b> on ${formattedDate} has been cancelled.`);
    if (reason) {
      text += `\n<i>${escapeHtml(reason)}</i>`;
    }
    text += `\n\nIf you'd like to rebook, times are open in the app.`;

    return {
      text,
      keyboard: bookUrl
        ? { inline_keyboard: [[{ text: 'Book again', web_app: { url: bookUrl }, style: 'primary' }]] }
        : undefined,
      parse_mode: 'HTML',
    };
  },

  /**
   * mvp-silent-moments D3: the business cancelled a scheduled event date this
   * person held a seat in or was waiting for. Names that date, read from the
   * occurrence row (a package keeps its other dates). Nothing about money: a paid
   * attendee's refund is the admin's action. Never pinned.
   */
  eventCancelled(params: {
    eventTitle: string;
    startsAt: Date | string;
    timezone: string;
    brandName: string;
    firstName?: string | null;
    seat: 'held' | 'waitlisted';
    /** The discreet-name rule of the booking messages: "your session" replaces the title. */
    hideNameInMessages?: boolean;
    /** The catalog (`/book`). */
    bookUrl: string;
  }): MessageTemplate {
    const when = bookingStatusDate(params.startsAt, params.timezone).toFormat("ccc, LLL d 'at' h:mm a");
    const hidden = params.hideNameInMessages === true;
    const title = escapeHtml(boundBookingFact(params.eventTitle, 120));
    const name = params.firstName?.trim() ? `${escapeHtml(params.firstName.trim())}, ` : '';
    const brand = escapeHtml(params.brandName);
    let sentence: string;
    if (params.seat === 'waitlisted') {
      const subject = hidden ? 'your session' : title;
      const lead = name ? `${name}${subject}` : subject.charAt(0).toUpperCase() + subject.slice(1);
      sentence = `${lead} on ${when}, which you were waiting for, has been cancelled.`;
    } else {
      sentence = `${name}${brand} cancelled ${hidden ? 'your session' : title} on ${when}.`;
    }
    return {
      text: `<b>Session cancelled</b>

${sentence}

Times for other sessions are open in the app.`,
      parse_mode: 'HTML',
      keyboard: { inline_keyboard: [[{ text: "See what's open", web_app: { url: params.bookUrl }, style: 'primary' }]] },
    };
  },

  /**
   * ban-frees-booking-time D5: the one message a banned client gets, listing the
   * sessions the ban cancelled (at most 40, then "and N more"), each in its own stored
   * timezone. Written proof for the client: no rebook invitation, no button, no money
   * line, never the ban reason.
   */
  bookingsCancelledByBan(params: {
    firstName?: string | null;
    businessName: string;
    sessions: Array<{ serviceName: string; startsAt: Date | string; timezone: string; hideNameInMessages?: boolean }>;
  }): MessageTemplate {
    const business = escapeHtml(params.businessName);
    const lead = params.firstName?.trim() ? `${escapeHtml(params.firstName.trim())}, your` : 'Your';
    // Telegram caps a message's text: the first 40 (the caller orders them by start), then a count.
    const lines = params.sessions.slice(0, BAN_MESSAGE_SESSION_LIMIT).map((session) => {
      const service = session.hideNameInMessages ? 'Appointment' : escapeHtml(boundBookingFact(session.serviceName, 120));
      return `• ${service} · ${bookingStatusDate(session.startsAt, session.timezone).toFormat("cccc, LLLL d 'at' h:mm a")}`;
    });
    const rest = params.sessions.length - BAN_MESSAGE_SESSION_LIMIT;
    if (rest > 0) lines.push(`and ${rest} more`);
    return {
      text: `<b>Bookings cancelled</b>\n\n${lead} upcoming bookings with ${business} have been cancelled:\n${lines.join('\n')}\n\n`
        + `If you have questions, please contact ${business} directly.`,
      parse_mode: 'HTML',
    };
  },

  /**
   * Booking approved but payment required notification to client.
   * Used when admin approves a booking that requires payment before confirmation.
   */
  bookingApprovedPaymentRequired(params: {
    serviceName: string;
    dateTime: Date | string;
    timezone: string;
    amount?: number | null;
    firstName?: string | null;
    /**
     * booking-detail-reskin (D8): deep link to `/booking-hub/booking/:id`. When
     * present the template owns a single "Complete payment" web_app button — the
     * page's PENDING_PAYMENT errand bar carries the client into checkout from there.
     */
    bookingDetailUrl?: string;
    /**
     * manual-booking-redesign D17: the restarted completion deadline of an approved
     * manual booking (`bookingDeadlineText` of `Booking.completeBy`); absent for every
     * other approved request.
     */
    deadline?: string | null;
    /** booking-truth-batch: a discreet offering is never named. */
    hideNameInMessages?: boolean;
  }): MessageTemplate {
    const { serviceName, dateTime, timezone, amount, bookingDetailUrl, firstName } = params;
    const formattedDate = formatDateTime(dateTime, timezone);
    const nameSuffix = firstName ? `, ${escapeHtml(firstName)}` : '';

    let text =
      `<b>Approved, one step left${nameSuffix}</b>\n\n` +
      `<b>${params.hideNameInMessages ? 'Your appointment' : escapeHtml(serviceName)}</b>\n` +
      `${formattedDate}\n\n`;

    if (amount != null && amount > 0) {
      text += `$${amount.toFixed(2)} secures your spot. It takes about a minute.`;
    } else {
      text += `One quick payment secures your spot. It takes about a minute.`;
    }
    if (params.deadline) text += `\n\nPlease pay by ${escapeHtml(params.deadline)} to keep this time.`;

    return {
      text,
      parse_mode: 'HTML',
      keyboard: bookingDetailUrl
        ? {
            inline_keyboard: [
              [{ text: 'Complete payment', web_app: { url: bookingDetailUrl }, style: 'primary' }],
            ],
          }
        : undefined,
    };
  },

  /**
   * manual-booking-redesign D7: an admin booked a time the client has to finish
   * (register, fill forms, pay) by the deadline. Never pinned. One adaptive
   * button; the approval variant waits on the admin's review after the forms.
   */
  bookingWaiting(params: BookingWaitingMessageData): MessageTemplate {
    const { items } = params;
    const name = params.firstName ? `, ${escapeHtml(boundBookingFact(params.firstName, BOOKING_FIRST_NAME_LENGTH))}` : '';
    const sender = escapeHtml(boundBookingFact(params.senderName, 120));
    const deadline = escapeHtml(params.deadline);
    const price = items.payment?.amount != null ? wholeDollars(items.payment.amount) : null;
    let body: string;
    if (items.approval) {
      const subject = items.forms.length > 0 ? formCount(items.forms.length) : 'Your registration';
      const them = items.forms.length + (items.registration ? 1 : 0) > 1 ? 'them' : 'it';
      const after = items.payment
        ? `you'll get a link to pay${price ? ` (${price})` : ''}`
        : 'it is booked';
      body = `${sender} saved this time for you. ${subject} to fill by ${deadline}, then ${sender} reviews ${them} and ${after}.`;
    } else if (items.forms.length === 0 && !items.payment) {
      body = `${sender} saved this time for you. Please register to confirm it by ${deadline} or the time opens back up.`;
    } else {
      const payment = items.payment ? `payment${price ? ` (${price})` : ''}` : '';
      const lock = items.forms.length > 0
        ? `${formCount(items.forms.length)}${payment ? ` and ${payment}` : ''} ${items.forms.length === 1 && !payment ? 'locks' : 'lock'} it in.`
        : `${payment.charAt(0).toUpperCase()}${payment.slice(1)} locks it in.`;
      body = `${sender} saved this time for you. ${lock} Please finish by ${deadline} or the time opens back up.`;
    }
    if (params.note) body += `\n\n${formatNoteLine(escapeHtml(params.note))}`;
    return {
      text: `<b>Almost booked${name}</b>\n${bookingSessionLine(params)}\n\n${body}`,
      parse_mode: 'HTML',
      keyboard: params.actionUrl
        ? { inline_keyboard: [[{ text: bookingWaitingLabel(items), web_app: { url: params.actionUrl }, style: 'primary' }]] }
        : undefined,
    };
  },

  /**
   * manual-booking-redesign D7: the closing edit of a waiting message, from its
   * stored copy: the text kept, the button gone, one italic note appended (the
   * `form_request_closed` contract). Past Telegram's limit only the button goes.
   */
  bookingWaitingClosed(sent: SentMessageCopy, outcome: BookingWaitingClosedOutcome, senderName: string): MessageTemplate {
    const line = {
      completed: 'Done. Your confirmation is below.',
      sent_for_approval: `Forms received. Waiting for ${escapeHtml(boundBookingFact(senderName, 120))}'s approval.`,
      released: 'This hold ended.',
      cancelled: 'Cancelled.',
    }[outcome];
    const html = sent.parseMode === null || sent.parseMode === 'HTML';
    const closed = `${sent.text}\n\n${html ? `<i>${line}</i>` : `_${line}_`}`;
    const visible = html ? getTelegramVisibleLengthFromHtml(closed) : closed.length;
    return {
      text: visible > 4096 ? sent.text : closed,
      ...(sent.parseMode && sent.parseMode !== 'plain' ? { parse_mode: sent.parseMode } : {}),
      keyboard: { inline_keyboard: [] },
    };
  },

  /**
   * manual-booking-redesign D7, D14: the completion deadline passed and the time
   * is open again. Nothing about forms (ruled 2026-10-07). Never pinned.
   */
  bookingReleased(params: Omit<BookingStatusFacts, 'bookingDetailUrl'> & { startText: string; bookUrl?: string }): MessageTemplate {
    // Chat mockup frame 6 (Gabriel 2026-10-07): the Booked session line with the
    // length; only the hold sentence carries the date and time.
    return {
      text: `<b>Time released</b>\n${bookedSessionLine(params)}\n\n` +
        `Your hold on ${escapeHtml(params.startText)} ended and the time is open again.`,
      parse_mode: 'HTML',
      keyboard: params.bookUrl ? { inline_keyboard: [[{ text: 'Book another time', web_app: { url: params.bookUrl }, style: 'primary' }]] } : undefined,
    };
  },

  /** Card refund receipt. Issuer timing is deliberately not promised. */
  paymentRefunded(params: { amountCents: number; currency: string; serviceName: string; hideNameInMessages?: boolean }): MessageTemplate {
    const subject = params.hideNameInMessages ? 'your appointment' : escapeHtml(params.serviceName);
    return {
      text: `<b>Refund on its way</b>\n\n${formatCurrency(params.amountCents, params.currency)} for ${subject}.`,
      parse_mode: 'HTML',
    };
  },

  /** Payment completed confirmation after admin approval (Flow 3). */
  bookingPaymentCompleted(params: BookingPaymentCompletedData): MessageTemplate {
    const { paymentInfo, bookingDetailUrl } = params;
    let text = bookingStatusHeading('Booked', params) + onlineFactLines(params.join) + phoneCallLines(params.phoneCall) + placeLines(params) + '\n\n';

    // Show payment summary based on whether balance remains
    if (paymentInfo.amountRemaining === 0) {
      text += `Paid: $${paymentInfo.amountPaid.toFixed(2)}`;
    } else {
      text += `Paid: $${paymentInfo.amountPaid.toFixed(2)}\n`;
      text += `Balance at session: $${paymentInfo.amountRemaining.toFixed(2)}`;
    }

    text += `\n\nYour receipt and booking details are in the app.`;

    return {
      text,
      keyboard: withJoinRow(withDirectionsRow(bookingDetailUrl ? BookingKeyboards.viewMyBooking(bookingDetailUrl) : undefined, params.directionsUrl), params.join),
      parse_mode: 'HTML',
    };
  },

  /**
   * A Moved notice. A virtual place reads "Now on Google Meet." ("Now online."
   * when the platform has no name, virtual-sessions-finish D3) and carries the
   * Join row above View my booking when `join` resolves a link (D7).
   */
  bookingMoved(params: BookingStatusFacts & { join?: BookingJoinFacts } & ({ kind: 'time'; oldStartAt: Date | string } |
    { kind: 'place'; locationText: string; isVirtual: boolean })): MessageTemplate {
    let body: string;
    if (params.kind === 'time') {
      body = `Was ${bookingStatusDate(params.oldStartAt, params.timezone).toFormat("cccc, LLL d 'at' h:mm a")}. Your booking is updated.`;
    } else if (params.isVirtual && !params.locationText.trim()) {
      body = 'Now online. Same time as before.';
    } else {
      body = `Now ${params.isVirtual ? 'on' : 'at'} ${escapeHtml(boundBookingFact(params.locationText, params.isVirtual ? 60 : 200))}. Same time as before.`;
    }
    return { text: bookingStatusHeading('Moved', params) + '\n\n' + body, parse_mode: 'HTML',
      keyboard: withJoinRow(withDirectionsRow(params.bookingDetailUrl ? BookingKeyboards.viewMyBooking(params.bookingDetailUrl) : undefined, params.directionsUrl), params.join) };
  },

  /**
   * virtual-sessions-finish D7, D10: the meeting link or passcode changed while
   * the platform did not. Its bold first line is exactly 25 characters, so the
   * pinned bar shows it whole; the session line is the waiting message's; the
   * passcode line sits above the sentence when it applies. Join is the only
   * button. Pinnable: it travels as a `booking_moved` intent of kind `link`.
   */
  meetingLinkChanged(params: Omit<BookingStatusFacts, 'duration' | 'bookingDetailUrl'> & { join: BookingJoinFacts }): MessageTemplate {
    const passcode = passcodeLine(params.join);
    return {
      text: `<b>Your meeting link changed</b>\n${bookingSessionLine(params)}\n\n` +
        `${passcode ? `${passcode}\n` : ''}Use the button below to join. Same time as before.`,
      parse_mode: 'HTML',
      keyboard: withJoinRow(undefined, params.join),
    };
  },

  /**
   * Session expired message - shown when flow expires naturally (TTL).
   * Directs user to use menu button to start a new session.
   */
  sessionExpired(bookUrl?: string): MessageTemplate {
    return {
      text: `That booking session timed out. No worries, nothing was booked.

Pick up right where you left off:`,
      keyboard: bookUrl
        ? { inline_keyboard: [[{ text: 'Book a session', web_app: { url: bookUrl }, style: 'primary' }]] }
        : { inline_keyboard: [] },
      parse_mode: 'HTML',
    };
  },
} as const;

// =============================================================================
// Service notification slots — pre-session reminders + post-session follow-ups
// =============================================================================

/**
 * Everything a reminder resolves from the booking at send time.
 *
 * `dateText` / `timeText` arrive PRE-FORMATTED in the client's timezone: the
 * template must never bake a timezone or a locale, and the admin editor renders
 * the same template with sample values so what it previews is what sends.
 */
export interface SessionReminderFacts {
  /** Resolved recipient name. Omitted ⇒ the greeting drops rather than reading "Hi ,". */
  firstName?: string | null;
  /**
   * Resolved offering name, or `null` when the tenant turned on
   * `hideNameInMessages` — the copy then says "your appointment".
   */
  serviceName: string | null;
  /** e.g. `Tue, Jul 14` */
  dateText: string;
  /** e.g. `2:00 PM` */
  timeText: string;
  durationMinutes?: number | null;
  /** Venue name/address line, or the virtual platform name. Null when unset. */
  locationText?: string | null;
  isVirtual?: boolean;
  /** How long before the session this slot fires. Drives the lead phrase only. */
  minutesBefore: number;
  /** The admin's optional note (Telegram HTML), appended under the facts. */
  note?: string | null;
  /** Preparation notes (already flattened to plain text) when the slot opts in. */
  preparationNotes?: string | null;
  preparationNotesLabel?: string | null;
  /** Actual remaining time at delivery; previews keep their configured lead. */
  actualLead?: string;
  /** Deep link for the [View my booking] button. Omitted ⇒ no button. */
  bookingDetailUrl?: string;
  /**
   * virtual-sessions-finish D11: the link resolved at delivery. Adds the Join
   * row above View my booking and the passcode line; previews leave it out.
   */
  join?: BookingJoinFacts;
  /** booking-directions-button: Google Maps directions for an in-person booking; previews leave it out. */
  directionsUrl?: string | null;
  /** service-by-phone D8: who calls and the client's number, read at delivery; replaces the place line. */
  phoneCall?: PhoneCall | null;
}

const MINUTES_PER_DAY = 1440;

/** Delivery wording uses the remaining time, including when a days-ahead slot is late. */
export function formatActualReminderLead(startsAt: Date, now: Date): string {
  const minutes = Math.max(1, Math.ceil((startsAt.getTime() - now.getTime()) / 60_000));
  const amount = minutes < 60 ? minutes : minutes < MINUTES_PER_DAY ? Math.round(minutes / 60) : Math.round(minutes / MINUTES_PER_DAY);
  const unit = minutes < 60 ? 'minute' : minutes < MINUTES_PER_DAY ? 'hour' : 'day';
  return `in about ${amount} ${unit}${amount === 1 ? '' : 's'}`;
}

/** "in about 30 minutes" · "in about an hour" · "in about 2 hours". */
export function formatStartingSoonLead(minutesBefore: number): string {
  if (minutesBefore <= 45) return `in about ${Math.max(1, Math.round(minutesBefore))} minutes`;
  if (minutesBefore <= 90) return 'in about an hour';
  return `in about ${Math.round(minutesBefore / 60)} hours`;
}

/** "tomorrow" · "in 3 days". */
export function formatReminderLead(minutesBefore: number): string {
  const days = Math.max(1, Math.round(minutesBefore / MINUTES_PER_DAY));
  return days === 1 ? 'tomorrow' : `in ${days} days`;
}

/** "your <b>Deep Tissue Massage</b>" · "your appointment" when the name is hidden. */
function subjectPhrase(serviceName: string | null): string {
  return serviceName ? `your <b>${escapeHtml(serviceName)}</b>` : 'your appointment';
}

function greeting(firstName?: string | null): string {
  const name = firstName?.trim();
  return name ? `Hi ${escapeHtml(name)}, ` : '';
}

/** Sentence-cases the opener when there is no name to lead with. */
function openSentence(firstName: string | null | undefined, rest: string): string {
  const lead = greeting(firstName);
  return lead ? `${lead}${rest}` : `${rest.charAt(0).toUpperCase()}${rest.slice(1)}`;
}

function locationLine(facts: SessionReminderFacts): string | null {
  const loc = facts.locationText?.trim();
  if (!loc) return facts.isVirtual ? 'Online' : null;
  return facts.isVirtual ? `Online on <b>${escapeHtml(loc)}</b>` : escapeHtml(loc);
}

/** The authored note is complete. Preparation is a separate outbox message. */
function withReminderNote(body: string, facts: SessionReminderFacts): string {
  const note = renderBookingReminderNote(facts.note, facts.firstName);
  return note ? `${body}\n\n${note}` : body;
}

export function renderBookingReminderNote(note?: string | null, firstName?: string | null): string {
  return sanitizeNotificationHtml((note ?? '').replaceAll('{firstName}',
    escapeHtml(boundBookingFact(firstName ?? '', BOOKING_FIRST_NAME_LENGTH))).trim());
}

function boundedReminderFacts(facts: SessionReminderFacts): SessionReminderFacts {
  return { ...facts,
    firstName: facts.firstName == null ? facts.firstName : boundBookingFact(facts.firstName, BOOKING_FIRST_NAME_LENGTH),
    serviceName: facts.serviceName == null ? null : boundBookingFact(facts.serviceName, 120),
    locationText: facts.locationText == null ? facts.locationText : boundBookingFact(facts.locationText, facts.isVirtual ? 60 : 200),
  };
}

/** location-notes-to-client: what a preparation message belongs to, named in its header. */
export interface PreparationAbout {
  /** The service or event name; null when the business hides it in messages. */
  name: string | null;
  dateTime: Date | string;
  timezone: string;
}

export const PreparationMessages = {
  /**
   * Silent pages of How to prepare, after a Booked message or a reminder. The first opens with the
   * label and the service with its date and time (Gabriel 2026-10-15), so it reads as belonging to
   * the message above; the rest continue the text.
   */
  pages(text: string, label: string | null | undefined, about: PreparationAbout): MessageTemplate[] {
    if (!text.trim()) return [];
    const name = about.name?.trim() ? boundBookingFact(about.name.trim(), 120) : 'Your appointment';
    const when = bookingStatusDate(about.dateTime, about.timezone).toFormat('ccc, LLL d, h:mm a');
    const heading = `<b>${escapeHtml(boundBookingFact(label?.trim() || 'How to prepare', 80))}</b>\n${escapeHtml(name)} · ${when}\n\n`;
    return splitPreparationText(text, 4096 - getTelegramVisibleLengthFromHtml(heading)).map((page, index) => ({
      text: (index === 0 ? heading : '') + escapeHtml(page), parse_mode: 'HTML', disableNotification: true,
    }));
  },

  /**
   * A sent page as the client's chat folds it (Gabriel 2026-10-23): the studio's notes alone, and the
   * label from the heading `pages` opened the first page with. A page without that heading is all notes.
   * Reads the stored HTML copy or its display form; both keep the heading as written.
   */
  foldOf(pageHtml: string): { label: string | null; notes: string } {
    const heading = PREPARATION_HEADING.exec(pageHtml);
    if (!heading) return { label: null, notes: pageHtml };
    return { label: unescapeHtml(heading[1]!), notes: pageHtml.slice(heading[0].length) };
  },
};

/** The heading `PreparationMessages.pages` writes: the label in bold, then the service and its date. */
const PREPARATION_HEADING = /^<b>([^<\n]*)<\/b>\n[^\n]*\n\n/;

/** Undoes `escapeHtml` (utils.ts), for a label read back out of a heading. */
function unescapeHtml(text: string): string {
  return text.replace(/&(amp|lt|gt|quot|#39);/g, (_, entity: string) => ({ amp: '&', lt: '<', gt: '>', quot: '"', '#39': "'" })[entity]!);
}

/**
 * The two pre-session reminder bodies and the two follow-up starters.
 *
 * CANONICAL COPY. The service-editor Notifications section renders these exact
 * templates with sample values as its locked "this is what sends" block, so the
 * copy exists in ONE place: change it here and both the preview and the send
 * change together. The admin can never edit a reminder body itself, only append
 * the note (`facts.note`) that lands under it (ruling D-05).
 *
 * NO SLOT SHIPS DEFAULT COPY (Gabriel 2026-08-13). The facts block below IS the
 * reminder message, so its note starts empty and stays optional; follow-ups
 * start empty too and the save door refuses to STORE an enabled one that is
 * still bodyless, which is what the retired starter copy was standing in for.
 */
export const SessionNotificationMessages = {
  /**
   * Short-fuse reminder (the ≤2h band, "Starting soon"). Deliberately one thought:
   * the session is imminent, here is when and where.
   */
  startingSoon(facts: SessionReminderFacts): MessageTemplate {
    facts = boundedReminderFacts(facts);
    const where = facts.locationText?.trim();
    // service-by-phone D8: "<b>10:00 AM</b> by phone." with the call line under it.
    const whenLine = facts.phoneCall
      ? `<b>${escapeHtml(facts.timeText)}</b> by phone.`
      : where
        ? facts.isVirtual
          ? `<b>${escapeHtml(facts.timeText)}</b> on <b>${escapeHtml(where)}</b>.`
          : `<b>${escapeHtml(facts.timeText)}</b> at <b>${escapeHtml(where)}</b>.`
        : facts.isVirtual
          ? `<b>${escapeHtml(facts.timeText)}</b> online.`
          : `<b>${escapeHtml(facts.timeText)}</b>.`;
    const passcode = passcodeLine(facts.join);

    const body =
      openSentence(
        facts.firstName,
        `${subjectPhrase(facts.serviceName)} starts ${facts.actualLead ?? formatStartingSoonLead(facts.minutesBefore)}.`,
      ) +
      `\n${whenLine}` + phoneCallLines(facts.phoneCall) + (passcode ? `\n${passcode}` : '');

    return {
      text: withReminderNote(body, facts),
      keyboard: withJoinRow(withDirectionsRow(facts.bookingDetailUrl
        ? BookingKeyboards.viewMyBooking(facts.bookingDetailUrl)
        : undefined, facts.directionsUrl), facts.join),
      parse_mode: 'HTML',
    };
  },

  /**
   * Days-ahead reminder ("Reminder"). Carries the full booking block, because at
   * this distance the client is planning around it rather than walking out the door.
   */
  reminder(facts: SessionReminderFacts): MessageTemplate {
    facts = boundedReminderFacts(facts);
    const opener = openSentence(
      facts.firstName,
      `this is a reminder about ${subjectPhrase(facts.serviceName)} ${facts.actualLead ?? formatReminderLead(facts.minutesBefore)}.`,
    );
    const duration = facts.durationMinutes ? ` · ${formatBookedLength(facts.durationMinutes)}` : '';
    const where = facts.phoneCall ? null : locationLine(facts);
    const passcode = passcodeLine(facts.join);
    const body =
      `${opener}\n\n` +
      `<b>${escapeHtml(facts.dateText)} · ${escapeHtml(facts.timeText)}</b>${duration}` +
      (where ? `\n${where}` : '') + phoneCallLines(facts.phoneCall) + (passcode ? `\n${passcode}` : '');

    return {
      text: withReminderNote(body, facts),
      keyboard: withJoinRow(withDirectionsRow(facts.bookingDetailUrl
        ? BookingKeyboards.viewMyBooking(facts.bookingDetailUrl)
        : undefined, facts.directionsUrl), facts.join),
      parse_mode: 'HTML',
    };
  },
} as const;

/**
 * Restriction messages sent to banned/suspended users via the bot (copy pass).
 * Bot-specific, softer wording than the API's `BANNED_MESSAGE`/`SUSPENDED_MESSAGE`
 * constants (which stay put for API-error consistency). When the caller passes the
 * tenant `brandName`, the copy names the business; otherwise a generic fallback.
 *
 * T-P5-D7-F2-1
 */
export const RestrictedMessages = {
  banned(brandName?: string | null): MessageTemplate {
    const brand = brandName ? escapeHtml(brandName) : null;
    return {
      text: brand
        ? `Your account with ${brand} is currently restricted. If you think this is a mistake, reach out to ${brand} directly.`
        : `Your account is currently restricted. If you think this is a mistake, please reach out.`,
      parse_mode: 'HTML' as const,
    };
  },
  suspended(brandName?: string | null): MessageTemplate {
    const brand = brandName ? escapeHtml(brandName) : 'us';
    return {
      text: `Your account is temporarily suspended and booking is paused. Contact ${brand} if you'd like to talk it through.`,
      parse_mode: 'HTML' as const,
    };
  },
};

/**
 * Outage-related messages for webhook replies during Telegram API unavailability.
 * These are plain text (no HTML) as webhook replies don't support parse_mode.
 */
export const OutageMessages = {
  /** Message shown when user sends a message/command during outage */
  messageReply:
    "Telegram is temporarily unavailable right now, so I can't complete that action. Please try again in a minute.",

  /** Message shown when user taps a button (callback query) during outage */
  callbackReply: 'Telegram is temporarily unavailable. Please try again shortly.',
} as const;

/** shop-self-fulfilment D2: a label that only repeats the name is left out, with its comma. */
const shopItems = (items: readonly ShopEmailItem[]) =>
  items
    .map((i) => {
      const option = shownOptionLabel(i.title, i.option);
      return `• ${escapeHtml(i.title)}${option ? `, ${escapeHtml(option)}` : ''}${i.quantity > 1 ? ` x ${i.quantity}` : ''}`;
    })
    .join('\n');
const shopCard = (card: string | null) => (card ? `your ${escapeHtml(card)}` : 'your card');
const shopRefundLine = (amount: string, card: string | null) =>
  `${formatUsd(amount)} is on its way back to ${shopCard(card)}. It can take 5 to 10 days to show on your statement.`;
const shopOrderButton = (orderUrl: string) => [{ text: 'See your order', url: orderUrl, style: 'primary' }];

function shopShipped(i: ShopShippedInput): MessageTemplate {
  const trackingNumber = !i.trackingUrl && i.trackingNumber
    ? `\nTracking number${i.carrier ? ` (${escapeHtml(i.carrier)})` : ''}: ${escapeHtml(i.trackingNumber)}`
    : '';
  const track = i.trackingUrl ? [[{ text: i.carrier ? `Track it with ${i.carrier}` : 'Track it', url: i.trackingUrl }]] : [];
  return {
    text: `Your order ${i.orderNumber} from <b>${escapeHtml(i.studioName)}</b> is on its way:\n${shopItems(i.items)}${trackingNumber}`,
    parse_mode: 'HTML',
    keyboard: { inline_keyboard: [...track, shopOrderButton(i.orderUrl)] },
  };
}

function shopRefunded(i: ShopRefundedInput): MessageTemplate {
  return {
    text: `<b>${escapeHtml(i.studioName)}</b> refunded ${formatUsd(i.amount)} to ${shopCard(i.card)} for order ${i.orderNumber}. `
      + `It can take 5 to 10 days to show on your statement.\n\n${i.stillShips ? 'Your order still ships.' : 'This order will not ship.'}`,
    parse_mode: 'HTML',
    keyboard: { inline_keyboard: [shopOrderButton(i.orderUrl)] },
  };
}

function shopRefundFailed(i: ShopRefundFailedInput): MessageTemplate {
  const studio = escapeHtml(i.studioName);
  return {
    text: `The ${formatUsd(i.amount)} refund <b>${studio}</b> sent to ${shopCard(i.card)} for order ${i.orderNumber} did not go through, `
      + `so that money has not come back to you. ${studio} has been told.`,
    parse_mode: 'HTML',
    keyboard: { inline_keyboard: [shopOrderButton(i.orderUrl)] },
  };
}

function shopProblem(i: ShopProblemInput): MessageTemplate {
  const studio = `<b>${escapeHtml(i.studioName)}</b>`;
  const lead = i.what === 'returned'
    ? `A package from your order ${i.orderNumber} with ${studio} was returned to the sender before it reached you:\n${shopItems(i.items)}`
    : i.wholeOrder
      ? `${studio} cancelled your order ${i.orderNumber}.`
      : `${studio} cancelled part of your order ${i.orderNumber}:\n${shopItems(i.items)}`;
  return {
    text: i.refund ? `${lead}\n\n${shopRefundLine(i.refund.amount, i.refund.card)}` : lead,
    parse_mode: 'HTML',
    keyboard: { inline_keyboard: [shopOrderButton(i.orderUrl)] },
  };
}

/**
 * studio-shop D8: a linked buyer's shop updates, each sent in place of its email
 * and rendered from the same stored facts. Worded for any studio; no em dashes;
 * each thing said once.
 */
export const ShopBuyerMessages = {
  forUpdate(email: ShopUpdateEmail): MessageTemplate {
    switch (email.kind) {
      case 'shop_shipped': return shopShipped(email.input);
      case 'shop_refunded': return shopRefunded(email.input);
      case 'shop_refund_failed': return shopRefundFailed(email.input);
      case 'shop_problem': return shopProblem(email.input);
    }
  },
};
